<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import {
  fetchShop, exchangeCash,
  type ExchangePackage, type ExchangeCustomTerms, type ExchangeResult,
} from '../game/shop-api';
import cashIcon from '../assets/fimbulwake/icons/cash.webp';
import runesIcon from '../assets/fimbulwake/icons/runes.webp';
import { toast } from '../game/toast';
import { confirmAction } from '../game/confirm';
import { t } from '../i18n';
import exchPocket from '../assets/fimbulwake/shop/exch-pocket.webp';
import exchBriefcase from '../assets/fimbulwake/shop/exch-briefcase.webp';
import exchDuffel from '../assets/fimbulwake/shop/exch-duffel.webp';

/** `spotlight` pulses the pack cards on entry (header cash chip → "here's how
 *  you get cash") — see HeroScreen's guide + `.guide-glow` in app.css. */
const props = defineProps<{ spotlight?: boolean }>();
const emit = defineEmits<{ (e: 'updated'): void; (e: 'topup'): void }>();

const packages = ref<ExchangePackage[]>([]);
const custom = ref<ExchangeCustomTerms | null>(null);
const commissionPct = ref(4);
const balance = ref<{ soft: number; premium: number } | null>(null);
const loading = ref(true);
const error = ref('');
const busy = ref<string | null>(null);
const justLaundered = ref<number | null>(null); // cash credited by the last exchange

onMounted(async () => {
  try {
    const v = await fetchShop();
    packages.value = v.exchange.packages;
    custom.value = v.exchange.custom;
    commissionPct.value = v.exchange.commissionPct;
    balance.value = v.balance;
    customBars.value = Math.max(v.exchange.custom.minBars, 100);
  }
  catch (e) { error.value = (e as Error).message; }
  finally { loading.value = false; }
});

// ── Custom amount — live quote at the base rate ──
const customBars = ref(100);
const customGross = computed(() =>
  custom.value ? customBars.value * custom.value.cashPerBar : 0);
const customCut = computed(() => Math.max(1, Math.round(customGross.value * commissionPct.value / 100)));
const customCredited = computed(() => customGross.value - customCut.value);
const customValid = computed(() =>
  !!custom.value && Number.isInteger(customBars.value)
  && customBars.value >= custom.value.minBars && customBars.value <= custom.value.maxBars);
const customAffordable = computed(() =>
  balance.value != null && customBars.value <= balance.value.premium);
/** How many runes the pick outruns the purse by — drives the shortfall notice. */
const customShortfall = computed(() =>
  balance.value == null ? 0 : Math.max(0, customBars.value - balance.value.premium));

/** Per-tier dressing — same visual system as the Gold Reserve's packs. */
interface TierLook {
  art: string; color: string;
  ribbon?: string; ribbonIcon?: string;
  card?: 'popular' | 'whale';
  btn: 'grey' | 'gold' | 'green';
}
/** Tier look -> button-system variant. The premium tiers earn the lit primary
 *  (.btn-forge--rune); the plain tiers stay a forged tab, so the price ladder
 *  still reads at a glance. Replaces four per-tier COLOUR skins (purple-black,
 *  brass, green, bright-brass) that were art-deco leftovers and had nothing to do
 *  with what the tiers mean. */
const buyVariant = (t: string) => (t === 'grey' || t === 'purple' ? '' : 'btn-forge--rune');

const TIERS: Record<string, TierLook> = {
  pocket:    { art: exchPocket,    color: '#c8a24c', btn: 'grey' },
  briefcase: { art: exchBriefcase, color: '#e3c878', btn: 'gold', ribbon: t('counting.popular'), ribbonIcon: '★', card: 'popular' },
  duffel:    { art: exchDuffel,    color: '#6f9e54', btn: 'green', ribbon: t('counting.bestRate'), ribbonIcon: '♛', card: 'whale' },
};
function tier(p: ExchangePackage, idx: number): TierLook {
  return TIERS[p.id] ?? Object.values(TIERS)[Math.min(idx, 2)];
}

function fmt(n: number): string { return n.toLocaleString('en-US'); }
function affordable(p: ExchangePackage): boolean {
  return balance.value != null && p.bars <= balance.value.premium;
}

function settle(res: ExchangeResult) {
  balance.value = res.balance;
  justLaundered.value = res.credited;
  toast(t('counting.toast', { amount: fmt(res.credited) }), { icon: cashIcon });
  emit('updated');
}

async function launder(p: ExchangePackage) {
  if (busy.value) return;
  const ok = await confirmAction({
    kicker: t('counting.confirm.kicker'),
    title: t('counting.confirm.weighInPackage', { name: p.name }),
    icon: tier(p, 0).art,
    rows: [
      { label: t('counting.confirm.runesIn'), value: fmt(p.bars), icon: runesIcon, tone: 'bright' },
      { label: t('counting.tally.hallCut', { pct: commissionPct.value }), value: `−${fmt(p.commission)}`, tone: 'error' },
      { label: t('counting.confirm.youGet'), value: fmt(p.credited), icon: cashIcon, tone: 'success' },
    ],
    confirmLabel: t('counting.weighIn'),
  });
  if (!ok) return;
  busy.value = p.id; error.value = ''; justLaundered.value = null;
  try { settle(await exchangeCash({ packageId: p.id })); }
  catch (e) { error.value = (e as Error).message; }
  finally { busy.value = null; }
}

async function launderCustom() {
  if (busy.value || !customValid.value) return;
  const ok = await confirmAction({
    kicker: t('counting.confirm.kicker'),
    title: t('counting.confirm.weighInAmount', { amount: fmt(customBars.value) }),
    icon: runesIcon,
    rows: [
      { label: t('counting.confirm.runesIn'), value: fmt(customBars.value), icon: runesIcon, tone: 'bright' },
      { label: t('counting.tally.hallCut', { pct: commissionPct.value }), value: `−${fmt(customCut.value)}`, tone: 'error' },
      { label: t('counting.confirm.youGet'), value: fmt(customCredited.value), icon: cashIcon, tone: 'success' },
    ],
    confirmLabel: t('counting.weighIn'),
  });
  if (!ok) return;
  busy.value = 'custom'; error.value = ''; justLaundered.value = null;
  try { settle(await exchangeCash({ bars: customBars.value })); }
  catch (e) { error.value = (e as Error).message; }
  finally { busy.value = null; }
}
</script>

<template>
  <section>
    <!-- header: kicker + gilt title, both balances chiselled into the right -->
    <div class="flex flex-wrap items-end justify-between gap-4 pb-4 border-b border-border/70">
      <div>
        <div class="font-label uppercase tracking-[0.08em] text-[9px] text-text-dim">{{ t('counting.kicker') }}</div>
        <h1 class="flex items-center gap-3 font-heading font-black text-[28px] gilt-text tracking-[0.02em] leading-tight">
          <img :src="cashIcon" class="w-7 h-7 ch-gem" alt="" />
          {{ t('market.tabs.weighing') }}
        </h1>
      </div>
      <div v-if="balance" class="flex flex-wrap items-center gap-2">
        <div class="cs-balance">
          <img :src="runesIcon" class="w-4 h-4" alt="" />
          <div>
            <div class="font-heading text-[10px] tracking-[0.1em] uppercase text-[#bccbdb] leading-none whitespace-nowrap">{{ t('shop.runesUnit') }}</div>
            <div class="font-heading font-black text-lg text-gold-bright leading-tight">{{ fmt(balance.premium) }}</div>
          </div>
        </div>
        <div class="cs-balance">
          <img :src="cashIcon" class="w-4 h-4" alt="" />
          <div>
            <div class="font-heading text-[10px] tracking-[0.1em] uppercase text-[#bccbdb] leading-none whitespace-nowrap">{{ t('common.silver') }}</div>
            <div class="font-heading font-black text-lg text-gold leading-tight">{{ fmt(balance.soft) }}</div>
          </div>
        </div>
      </div>
    </div>

    <p class="font-body italic text-sm text-text-dim mt-3 mb-8">
      Runes go in, hacksilver comes out — the hall keeps {{ commissionPct }}% off the top.
      Bigger hauls weigh in at a better rate.
    </p>

    <p v-if="loading" class="text-text-dim">{{ t('common.loading') }}</p>
    <p v-if="error" class="mb-3 text-sm cs-err">{{ error }}</p>

    <template v-if="!loading">
      <!-- inline settle banner -->
      <div v-if="justLaundered !== null" class="cs-granted">
        <img :src="cashIcon" class="w-6 h-6 ch-gem" alt="" />
        <span><strong class="text-gold-bright">+{{ fmt(justLaundered) }}</strong> {{ t('counting.addedToPurse') }}</span>
      </div>

      <!-- ── THE WEIGHING STONE — one continuous granite counter. The three tiers
           stand on it as objects of increasing size, not cards in a grid; the
           "best rate" tier is biggest and sits over its own ember pool. Every
           tier's rate plaque and cost row share the SAME fixed heights, so the
           baseline and right edge line up across all three without a single
           tuned percentage. ─────────────────────────────────────────────────── -->
      <div class="cs-counter bound-frame relative overflow-hidden mb-10">
        <div class="cs-frost" aria-hidden="true"></div>
        <div class="cs-row">
          <div
            v-for="(p, idx) in packages" :key="p.id"
            class="cs-tier"
            :class="[`cs-tier--${idx}`, {
              'cs-tier--popular': tier(p, idx).card === 'popular',
              'cs-tier--best': tier(p, idx).card === 'whale',
              'guide-glow': props.spotlight,
            }]"
          >
            <div v-if="tier(p, idx).ribbon" class="cs-ribbon">
              <span class="cs-ribbon-ic">{{ tier(p, idx).ribbonIcon }}</span>{{ tier(p, idx).ribbon }}
            </div>
            <div class="cs-ember" aria-hidden="true"></div>

            <div class="cs-stage">
              <img :src="tier(p, idx).art" :alt="p.name" class="cs-art" />
            </div>

            <div class="cs-name">{{ p.name }}</div>

            <div class="cs-plaque">
              <span class="cs-plaque-label">{{ t('counting.cleanSilver') }}</span>
              <span class="cs-plaque-value">{{ fmt(p.credited) }}</span>
            </div>
            <div class="cs-break">
              <span>{{ t('counting.gross', { amount: fmt(p.gross) }) }}</span>
              <span class="cs-break-err">&minus;{{ fmt(p.commission) }}</span>
            </div>

            <div class="cs-cost">
              <img :src="runesIcon" alt="" />
              {{ fmt(p.bars) }} runes
            </div>
            <button
              class="btn-forge btn-forge--sm cs-buy" :class="buyVariant(tier(p, idx).btn)"
              :disabled="busy !== null || !affordable(p)"
              :title="affordable(p) ? undefined : t('counting.notEnough')"
              @click="launder(p)"
            >{{ busy === p.id ? '…' : t('counting.weighIn') }}</button>
          </div>
        </div>
      </div>

      <!-- ── THE WEIGHMASTER'S SLATE — custom amount, any number of bars at the
           base rate. The number field is a chiselled slot, not a text box; the
           quote is three tallies cut into the slate. ────────────────────────── -->
      <div v-if="custom" class="cs-slate bound-frame surf-plank relative">
        <div class="cs-slate-head">
          <div class="font-label uppercase tracking-[0.08em] text-[9px] text-text-dim">{{ t('counting.slate') }}</div>
          <div class="font-heading text-lg text-gold mt-1">{{ t('counting.customAmount') }}</div>
          <div class="font-body text-xs text-text-dim mt-1">
            Any amount from {{ fmt(custom.minBars) }} to {{ fmt(custom.maxBars) }} runes —
            base rate, the bundles above pay better per rune.
          </div>
        </div>
        <div class="cs-slate-body">
          <!-- Full server range, NOT capped at the purse: capping made min==max
               on an empty purse and froze the slider dead. Dreaming past the
               balance is allowed — the shortfall notice below carries the why. -->
          <input
            type="range" class="cs-slider"
            :min="custom.minBars" :max="custom.maxBars" step="10"
            v-model.number="customBars"
          />
          <div class="cs-chisel">
            <img :src="runesIcon" class="w-4 h-4" alt="" />
            <input
              type="number" class="cs-chisel-input"
              :min="custom.minBars" :max="custom.maxBars" v-model.number="customBars"
            />
          </div>
          <div class="cs-tally">
            <div class="cs-tally-row">
              <span>{{ t('counting.tally.gross') }}</span><b>{{ fmt(customGross) }}</b>
            </div>
            <div class="cs-tally-row">
              <span>{{ t('counting.tally.hallCut', { pct: commissionPct }) }}</span><b class="cs-tally-err">&minus;{{ fmt(customCut) }}</b>
            </div>
            <div class="cs-tally-row cs-tally-row--net">
              <span>{{ t('counting.tally.credited') }}</span><b>{{ fmt(customCredited) }}</b>
            </div>
          </div>
          <button
            class="btn-forge btn-forge--rune btn-forge--sm cs-slate-buy"
            :disabled="busy !== null || !customValid || !customAffordable"
            :title="!customAffordable && customValid ? t('counting.shortOnRunes') : undefined"
            @click="launderCustom"
          >{{ busy === 'custom' ? '…' : t('counting.weighInCustom') }}</button>
        </div>
        <!-- the shortfall, said out loud — the button keeps its verb and the
             notice carries the reason plus the way out -->
        <Transition name="cs-short">
          <div v-if="customValid && !customAffordable" class="cs-short" role="status">
            <img :src="runesIcon" alt="" />
            <span>{{ t('counting.shortNotice', { n: fmt(customShortfall) }) }}</span>
            <button class="btn-forge btn-forge--carved btn-forge--sm" @click="emit('topup')">{{ t('counting.getRunes') }}</button>
          </div>
        </Transition>
        <p v-if="!customValid" class="font-body text-[11px] cs-err mt-2">
          Enter a whole number between {{ fmt(custom.minBars) }} and {{ fmt(custom.maxBars) }}.
        </p>
      </div>

      <!-- short on bars? the reserve is next door -->
      <div class="mt-6 flex items-center gap-3">
        <span class="font-body italic text-sm text-text-dim">{{ t('counting.hoardThin') }}</span>
        <button class="btn-forge btn-forge--carved btn-forge--sm" @click="emit('topup')">+ Buy runes</button>
      </div>
    </template>
  </section>
</template>

<style scoped>
.ch-gem { animation: ch-gem-pulse 2.6s ease-in-out infinite; }
@keyframes ch-gem-pulse {
  0%, 100% { filter: drop-shadow(0 0 2px rgba(227, 200, 120, 0.25)); transform: scale(1); }
  50% { filter: drop-shadow(0 0 9px rgba(227, 200, 120, 0.75)); transform: scale(1.06); }
}

/* a value slot chiselled into metal, not a flat rgba box */
.cs-balance {
  display: flex; align-items: center; gap: 8px;
  padding: 7px 12px;
  clip-path: var(--clip-hewn);
  background: var(--sunken);
  box-shadow: var(--rim-ice-sunken);
}

/* the hall confirms — same chiselled-slot material as the balances above,
   with a gilt hairline since this one is good news */
.cs-granted {
  display: flex; align-items: center; justify-content: center; gap: 10px;
  margin-bottom: 16px;
  padding: 10px 16px;
  clip-path: var(--clip-hewn);
  background: var(--sunken);
  box-shadow: var(--rim-ice-sunken), inset 0 0 0 1px rgba(201, 165, 88, 0.28);
  font-family: var(--font-body, serif);
  font-size: 15px;
  color: var(--color-text);
}

/* a lighter red for anything sitting on the dark stone/sunken materials —
   the base --color-error fails 4.5:1 there (2.7:1 measured); this is the same
   fix Arena.vue already uses for its loss badge. */
.cs-err { color: #d98a8a; }

/* the shortfall notice — an ember-lit strip under the slate's controls,
   not an error box: wanting more than the purse holds is a plan, not a fault */
.cs-short {
  display: flex; align-items: center; gap: 10px;
  margin-top: 12px; padding: 8px 12px;
  background: linear-gradient(90deg, color-mix(in srgb, var(--color-ember, #d9873c) 14%, transparent), rgba(0, 0, 0, 0.25) 70%);
  border: 1px solid color-mix(in srgb, var(--color-ember, #d9873c) 40%, transparent);
  box-shadow: inset 0 1px 0 rgba(226, 236, 248, 0.06), 0 0 18px color-mix(in srgb, var(--color-ember, #d9873c) 12%, transparent);
}
.cs-short img { width: 18px; height: 18px; flex-shrink: 0; filter: drop-shadow(0 0 6px color-mix(in srgb, var(--color-ember, #d9873c) 45%, transparent)); }
.cs-short span {
  font-family: var(--font-body, serif); font-size: 13px; line-height: 1.35;
  color: var(--color-text, #dbe5ef); min-width: 0; flex: 1;
}
.cs-short .btn-forge { flex-shrink: 0; }
.cs-short-enter-active, .cs-short-leave-active { transition: opacity 200ms ease, transform 200ms ease; }
.cs-short-enter-from, .cs-short-leave-to { opacity: 0; transform: translateY(-4px); }
@media (prefers-reduced-motion: reduce) {
  .cs-short-enter-active, .cs-short-leave-active { transition: none; }
}
@media (max-width: 480px) {
  .cs-short { flex-wrap: wrap; }
  .cs-short .btn-forge { width: 100%; }
}

/* ══ THE WEIGHING STONE ═══════════════════════════════════════════════════
   Granite (--tex-stone) blended soft-light into a single dark gradient, in
   ONE background stack — a texture on its own pseudo-element with nothing
   beneath it in that same stack is a no-op (the trap this system already
   paid for). bound-frame supplies the gilt rim + corner knots on top. ────── */
.cs-counter {
  padding: 30px clamp(14px, 4vw, 44px) 0;
  background:
    var(--tex-stone),
    radial-gradient(130% 70% at 50% -10%, rgba(226, 236, 248, 0.07), transparent 55%),
    linear-gradient(180deg, #3c424b 0%, #272d36 45%, #171b21 100%);
  background-size: 190px 190px, auto, auto;
  background-blend-mode: soft-light, normal, normal;
}
/* frost hanging off the counter's top edge — same device as every bound-frame's
   top rim elsewhere in the system (hg-frost, scr-head-win) */
.cs-frost {
  position: absolute; inset-inline: 0; top: 0; height: 15px;
  z-index: 3;
  pointer-events: none;
  background: var(--orn-frost-ice) repeat-x left top;
  opacity: 0.85;
}

.cs-row {
  position: relative;
  display: flex;
  align-items: stretch; /* the ONE shared value: every tier stretches to the
                            tallest tier's natural height, so their fixed-height
                            rows below line up without a tuned percentage */
  gap: clamp(10px, 3vw, 30px);
  padding-bottom: 28px;
}

.cs-tier {
  position: relative;
  flex: 1 1 0;
  min-width: 148px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
/* asymmetry over uniform grid: pocket narrow, briefcase middling, duffel widest
   — width tracks the size of the object standing on it */
.cs-tier--0 { flex-grow: 0.85; }
.cs-tier--1 { flex-grow: 1.05; }
.cs-tier--2 { flex-grow: 1.35; }
.cs-tier--best { transform: translateY(-3px); }

.cs-ribbon {
  position: absolute; top: -1px; left: -1px;
  z-index: 3;
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 9px;
  font-family: var(--font-heading, serif);
  font-size: 8px; font-weight: 700;
  letter-spacing: 0.07em; text-transform: uppercase;
  background: var(--gilt-rim);
  color: #1a1206;
  white-space: nowrap;
}
.cs-ribbon-ic { font-size: 9px; line-height: 1; }

/* the ember pool under the best-rate tier — the ONE warm object in an
   otherwise cold counter */
.cs-ember {
  position: absolute; left: 50%; bottom: 0; z-index: 0;
  width: 76%; height: 46%;
  max-width: 220px;
  transform: translateX(-50%);
  pointer-events: none;
  opacity: 0;
  background: radial-gradient(closest-side, color-mix(in srgb, var(--color-ember) 55%, transparent), transparent 72%);
  filter: blur(1px);
  transition: opacity 260ms ease;
}
.cs-tier--best .cs-ember { opacity: 0.9; }

/* the object standing on the counter — bottom-anchored, so shorter pouches and
   the tall duffel all rest on the same ground line */
.cs-stage {
  position: relative; z-index: 2;
  flex: 1 1 auto;
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  min-height: 64px;
}
.cs-art {
  object-fit: contain;
  filter: drop-shadow(0 14px 18px rgba(0, 0, 0, 0.65));
  transition: filter 200ms ease;
}
.cs-tier--0 .cs-art { width: clamp(64px, 9vw, 78px); height: clamp(64px, 9vw, 78px); }
.cs-tier--1 .cs-art { width: clamp(88px, 11vw, 114px); height: clamp(88px, 11vw, 114px); }
.cs-tier--2 .cs-art { width: clamp(120px, 15vw, 158px); height: clamp(120px, 15vw, 158px); }
.cs-tier--best .cs-art { filter: drop-shadow(0 16px 22px rgba(0, 0, 0, 0.7)) drop-shadow(0 0 20px color-mix(in srgb, var(--color-ember) 35%, transparent)); }

/* the ground line — the counter's own carved seam, painted twice 1px apart:
   dark groove above, light burr below. Identical on all three tiers, and
   because every column shares the same stretched height, the three segments
   sit on one continuous line rather than three independently-tuned ones. */
.cs-name {
  position: relative; z-index: 1;
  width: 100%;
  margin-top: 8px;
  padding-top: 9px;
  border-top: 1px solid rgba(4, 6, 10, 0.55);
  box-shadow: inset 0 1px 0 rgba(226, 236, 248, 0.08);
  font-family: var(--font-label, sans-serif);
  font-size: 10.5px; letter-spacing: 0.06em; text-transform: uppercase;
  color: var(--color-gold);
}

/* the rate, struck into the counter's face — one fixed height for every tier,
   so the figure's baseline and right edge line up regardless of digit count */
.cs-plaque {
  position: relative; z-index: 1;
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  width: 100%;
  height: 46px;
  margin-top: 10px;
  padding: 0 12px;
  clip-path: var(--clip-hewn);
  background: var(--sunken);
  box-shadow: var(--rim-ice-sunken);
}
.cs-plaque-label {
  font-family: var(--font-label, sans-serif);
  font-size: 9px; letter-spacing: 0.06em; text-transform: uppercase;
  color: #bccbdb;
  text-align: left;
}
.cs-plaque-value {
  font-family: var(--font-heading, serif); font-weight: 700;
  color: var(--color-gold-bright);
  text-shadow: 0 -1px 0 rgba(0, 0, 0, 0.85), 0 1px 0 rgba(226, 236, 248, 0.1);
  font-variant-numeric: tabular-nums;
  text-align: right;
  white-space: nowrap;
}
.cs-tier--0 .cs-plaque-value { font-size: 20px; }
.cs-tier--1 .cs-plaque-value { font-size: 23px; }
.cs-tier--2 .cs-plaque-value { font-size: 26px; }

.cs-break {
  position: relative; z-index: 1;
  display: flex; justify-content: space-between; gap: 8px;
  width: 100%;
  margin-top: 6px;
  font-family: var(--font-body, serif);
  font-size: 11px;
  color: #bccbdb;
  font-variant-numeric: tabular-nums;
}
.cs-break-err { color: #d98a8a; }

.cs-cost {
  position: relative; z-index: 1;
  display: inline-flex; align-items: center; gap: 6px;
  margin-top: 10px; margin-bottom: 12px;
  font-family: var(--font-heading, serif);
  font-size: 15px; font-weight: 900;
  color: #fff;
  font-variant-numeric: tabular-nums;
}
.cs-cost img { width: 16px; height: 16px; object-fit: contain; }

.cs-buy { position: relative; z-index: 1; margin-top: auto; width: 100%; }

/* ══ THE WEIGHMASTER'S SLATE ══════════════════════════════════════════════ */
.cs-slate { padding: 18px 20px; }
.cs-slate-head { margin-bottom: 14px; }
.cs-slate-body { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
.cs-slider { flex: 1 1 240px; min-width: 180px; accent-color: var(--color-gold); cursor: pointer; }

/* the chisel slot — a socket cut into the slate, not a bordered text box */
.cs-chisel {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 7px 12px;
  border: 0;
  clip-path: var(--clip-hewn);
  background: var(--sunken);
  box-shadow: var(--rim-ice-sunken);
  transition: box-shadow 180ms ease;
}
.cs-chisel:focus-within {
  box-shadow: var(--rim-ice-sunken), inset 0 0 16px color-mix(in srgb, var(--color-aurora) 24%, transparent);
}
.cs-chisel-input {
  width: 88px; background: transparent; border: 0; outline: none;
  font-family: var(--font-heading, serif); font-size: 15px; color: var(--color-gold-bright);
  font-variant-numeric: tabular-nums;
}
.cs-chisel-input::-webkit-outer-spin-button, .cs-chisel-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }

/* the carved tallies — hairline groove + light lip, label left / value right */
.cs-tally {
  display: flex; flex-direction: column;
  min-width: 160px;
  font-family: var(--font-body, serif); font-size: 12px;
  font-variant-numeric: tabular-nums;
}
.cs-tally-row {
  display: flex; align-items: baseline; justify-content: space-between; gap: 12px;
  padding: 5px 2px;
  border-top: 1px solid rgba(4, 6, 10, 0.5);
  box-shadow: inset 0 1px 0 rgba(226, 236, 248, 0.07);
  color: #bccbdb;
}
.cs-tally-row:first-child { border-top: 0; box-shadow: none; }
.cs-tally-row b { font-family: var(--font-heading, serif); font-weight: 700; color: var(--color-text); }
.cs-tally-err { color: #d98a8a; }
.cs-tally-row--net b { color: var(--color-gold-bright); font-size: 14px; }

.cs-slate-buy { width: auto; min-width: 150px; margin-top: 0; padding: 9px 18px; }

@media (max-width: 900px) {
  .cs-tier { min-width: 130px; }
}
/* Mobile: the 3 tiers become a compact 2-up grid (pocket + briefcase share a
   row) with the best-rate duffel spanning both columns beneath, instead of
   each tier stacking as its own full-width ~600px-tall block. */
@media (max-width: 700px) {
  .cs-counter { padding: 20px 10px 0; }
  .cs-row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 10px;
    row-gap: 26px;
    padding-bottom: 20px;
  }
  .cs-tier { min-width: 0; }
  .cs-tier--best { grid-column: 1 / -1; transform: none; }
  .cs-tier--0 .cs-art, .cs-tier--1 .cs-art { width: 92px; height: 92px; }
  .cs-tier--2 .cs-art { width: 116px; height: 116px; }
  .cs-name { font-size: 10px; margin-top: 6px; padding-top: 7px; }
  .cs-plaque { height: 40px; padding: 0 9px; }
  .cs-plaque-label { font-size: 8px; }
  .cs-tier--0 .cs-plaque-value,
  .cs-tier--1 .cs-plaque-value,
  .cs-tier--2 .cs-plaque-value { font-size: 20px; }
  .cs-break { font-size: 10px; }
  .cs-cost { font-size: 13px; margin-top: 8px; margin-bottom: 10px; }
  .cs-ribbon { font-size: 7px; padding: 3px 7px; }
  /* the custom panel stays one full-width block (per spec) — just tighten it */
  .cs-slate { padding: 14px 14px; }
  .cs-slate-body { gap: 10px; }
}

@media (prefers-reduced-motion: reduce) {
  .ch-gem { animation: none; }
  .cs-art, .cs-ember, .cs-chisel { transition: none; }
}
</style>
